import React, { useState, useEffect, useRef } from "react";
import { compose, graphql } from "react-apollo";
import {
  Button,
} from 'reactstrap';
import { withQuery } from "containers/withQuery";
import styled from "styled-components";
import { withSnackbarConsumer, Color } from "contexts/SnackbarContext/SnackbarContext.jsx";
import Dialog from "../../Dialog/Dialog";
import { UPDATE_DEVICE_MUTATION } from "./mutations";
import { DEVICES_QUERY } from "./queries";

const DropArea = styled.div`
  border: 2px dashed #ccc;
  border-radius: 6px;
  padding: 30px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &.active {
    border-color: #4c9aff;
    background: #f5f9ff;
  }
`;

const Preview = styled.img`
  max-width: 100%;
  max-height: 45vh;
  object-fit: contain;
  margin-top: 15px;
  border-radius: 4px;
`;

const MAX_SIZE = 10 * 1024 * 1024; // 10MB

const UploadImageModal = ({
  isOpen,
  toggleModal,
  devices,
  updateDevice,
  snackbarContext,
}) => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [deviceId, setDeviceId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef(null);

  const validateAndSetFile = (selectedFile) => {
    if (!selectedFile) return;

    if (!selectedFile.type.startsWith("image/")) {
      setError("Arquivo não é uma imagem válida");
      return;
    }

    if (selectedFile.size > MAX_SIZE) {
      setError("Imagem maior que 10MB");
      return;
    }

    setFile(selectedFile);
    setError(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    validateAndSetFile(droppedFile);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleFileInputChange = (e) => {
    validateAndSetFile(e.target.files[0]);
  };

  const handleUpload = () => {
    try {
      updateDevice({
        variables: {
          id: deviceId,
          params: {
            plantImage: file,
          },
        },
      });

      snackbarContext.setNotificationTimeOut(
        Color.success,
        "Imagem de fundo atualizada."
      );
      toggleModal();
      setFile(null);
    } catch (err) {
      snackbarContext.setNotificationTimeOut(
        Color.danger,
        err.message || "Erro ao enviar imagem"
      );
      toggleModal();
      setFile(null);
    }
  };

  useEffect(() => {
    if (devices && devices.length > 0) {
      setDeviceId(devices[0].id);
    }
  }, [devices]);

  useEffect(() => {
    const preventDefault = (e) => e.preventDefault();
    window.addEventListener("dragover", preventDefault);
    window.addEventListener("drop", preventDefault);
    return () => {
      window.removeEventListener("dragover", preventDefault);
      window.removeEventListener("drop", preventDefault);
    };
  }, []);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={toggleModal}
      title="Atualizar Imagem de Fundo"
      width={file ? "70vw" : "50vw"}
      maxWidth="90vw"
      maxHeight="90vh"
      footer={(
        <Button
          color="info"
          disabled={error}
          onClick={handleUpload}
        >
          Salvar
        </Button>
      )}
    >
      <DropArea
        className={isDragging ? "active" : ""}
        onClick={() => fileInputRef.current.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleFileInputChange}
        />

        {file ? (
          <React.Fragment>
            <strong>{file.name}</strong>
            <div>{`${(file.size / 1024 / 1024).toFixed(2)} MB`}</div>
            <Preview src={URL.createObjectURL(file)} />
          </React.Fragment>
        ) : (
          <p>Arraste uma imagem aqui ou clique para selecionar</p>
        )}
      </DropArea>

      {error && <p style={{ color: "red", marginTop: 10 }}>{error}</p>}

    </Dialog>
  );
};

export default compose(
  withQuery(() => ({
    query: DEVICES_QUERY,
    noLoader: true,
  })),
  graphql(UPDATE_DEVICE_MUTATION, { name: "updateDevice", }),
  withSnackbarConsumer
)(UploadImageModal);
